/**
 * Network Header Helpers for Dental PPO Export
 *
 * Handles the column group hierarchy for coverages with network sub-columns
 * (In Network / Out of Network). Extracts multi-level header rows and
 * provides cell merge ranges so the exported xlsx reflects:
 *
 * 1. Policy period headers (Current, Alternate, New Business) as a top row
 *    merged across their network sub-columns.
 * 2. Network sub-headers (In Network, Out Of Network) as the row below.
 * 3. Merged cells for network-agnostic benefit rows (where the In/Out
 *    distinction doesn't apply).
 */

import { Column, ColumnGroup } from "ag-grid-community";
import { Worksheet } from "exceljs";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HeaderCell {
  /** Display text for this header cell */
  label: string;
  /** 1-based Excel column where this cell starts */
  colStart: number;
  /** Number of leaf columns this header spans */
  span: number;
}

// ---------------------------------------------------------------------------
// Header hierarchy
// ---------------------------------------------------------------------------

/**
 * Determines whether the given leaf columns have any column group parents,
 * indicating a multi-level header structure exists (e.g., Dental PPO with
 * policy period → network sub-columns).
 */
export const hasColumnGroupHierarchy = (columns: Column[]): boolean =>
  columns.some((col) => col.getParent() !== null);

/**
 * Collects the parent chain for a single column, returned root-first.
 * e.g. [policyPeriodGroup, networkGroup] for a 3-tier Dental PPO column.
 */
const getParentChain = (col: Column): ColumnGroup[] => {
  const chain: ColumnGroup[] = [];
  let current: ColumnGroup | null = col.getParent();
  while (current) {
    chain.push(current);
    current = current.getParent();
  }
  return chain.reverse();
};

/**
 * Gets the header label for a group at a given depth in a column's chain.
 * Falls back to the leaf column's own header when the chain is shorter
 * than the requested depth (e.g., the pinned "Coverage Opportunity" column).
 */
const getGroupLabel = (
  chain: ColumnGroup[],
  depth: number,
  leafColumn: Column,
): string =>
  depth < chain.length
    ? (chain[depth].getColGroupDef()?.headerName ?? "")
    : leafColumn.getColDef().headerName || "";

/**
 * Gets the group reference at a given depth, or null if the column has no
 * group at that depth. Used for identity comparison to detect run boundaries.
 */
const getGroupAtDepth = (
  chain: ColumnGroup[],
  depth: number,
): ColumnGroup | null => (depth < chain.length ? chain[depth] : null);

// ---------------------------------------------------------------------------
// Run-length collapsing
// ---------------------------------------------------------------------------

interface GroupEntry {
  group: ColumnGroup | null;
  label: string;
}

/**
 * Detects run boundaries in a list of group entries. A new run starts
 * whenever the ColumnGroup reference changes (or is null, meaning ungrouped).
 *
 * Returns the starting index of each run.
 */
const findRunBoundaries = (entries: GroupEntry[]): number[] =>
  entries.reduce<number[]>((boundaries, entry, idx) => {
    if (idx === 0) return [0];
    const prev = entries[idx - 1];
    const isSameGroup = entry.group !== null && entry.group === prev.group;
    return isSameGroup ? boundaries : [...boundaries, idx];
  }, []);

/**
 * Collapses a flat list of per-column entries at one depth level into
 * merged HeaderCells by comparing consecutive group references.
 *
 * Columns sharing the same ColumnGroup instance are merged into a single
 * HeaderCell that spans all of them.
 */
const collapseIntoHeaderCells = (entries: GroupEntry[]): HeaderCell[] => {
  const boundaries = findRunBoundaries(entries);

  return boundaries.map((startIdx, runIdx) => {
    const nextStart =
      runIdx + 1 < boundaries.length ? boundaries[runIdx + 1] : entries.length;

    return {
      label: entries[startIdx].label,
      colStart: startIdx + 1, // 1-based Excel column
      span: nextStart - startIdx,
    };
  });
};

/**
 * Builds HeaderCells for one group depth level across all leaf columns.
 */
const buildHeaderLevelAtDepth = (
  columns: Column[],
  parentChains: ColumnGroup[][],
  depth: number,
): HeaderCell[] => {
  const entries = columns.map((col, idx) => ({
    group: getGroupAtDepth(parentChains[idx], depth),
    label: getGroupLabel(parentChains[idx], depth, col),
  }));

  return collapseIntoHeaderCells(entries);
};

/**
 * Builds the leaf-level HeaderCells (one per column, no merging).
 */
const buildLeafHeaderLevel = (columns: Column[]): HeaderCell[] =>
  columns.map((col, idx) => ({
    label: col.getColDef().headerName || "",
    colStart: idx + 1,
    span: 1,
  }));

/**
 * Walks each leaf column's parent chain and builds one array of HeaderCells
 * per depth level. Index 0 is the top-most group level (e.g., policy period)
 * and the last entry is the leaf column level (e.g., network names).
 *
 * Only call this when `hasColumnGroupHierarchy` returns true — otherwise
 * the standard single-row header is sufficient.
 */
export const buildGroupedHeaderLevels = (columns: Column[]): HeaderCell[][] => {
  const parentChains = columns.map(getParentChain);
  const maxDepth = Math.max(...parentChains.map((chain) => chain.length));

  const groupLevels = Array.from({ length: maxDepth }, (_, depth) =>
    buildHeaderLevelAtDepth(columns, parentChains, depth),
  );

  return [...groupLevels, buildLeafHeaderLevel(columns)];
};

// ---------------------------------------------------------------------------
// Network merge ranges
// ---------------------------------------------------------------------------

/**
 * Detects pairs of consecutive leaf columns that belong to the same plan
 * but represent different networks (e.g., `{planId}.inNetwork` and
 * `{planId}.outOfNetwork`).
 *
 * Returns [startCol, endCol] pairs (1-based, inclusive) that should be
 * merged on data rows where the benefit is not network-dependent.
 */
export const detectNetworkMergeRanges = (
  columns: Column[],
): [number, number][] => {
  const ranges: [number, number][] = [];
  let i = 0;

  while (i < columns.length) {
    const field = columns[i].getColDef().field;

    // Network columns have dotted fields like "{planId}.inNetwork"
    if (field && field.includes(".")) {
      const planId = field.split(".")[0];
      const start = i;

      // Collect all consecutive columns for the same plan
      while (
        i < columns.length &&
        columns[i].getColDef().field?.startsWith(`${planId}.`)
      ) {
        i++;
      }

      if (i - start > 1) {
        // +1 because Excel columns are 1-based
        ranges.push([start + 1, i]);
      }
    } else {
      i++;
    }
  }

  return ranges;
};

// ---------------------------------------------------------------------------
// Worksheet header writing
// ---------------------------------------------------------------------------

/**
 * Writes multi-level header rows to the worksheet with merged cells.
 * Each level becomes one Excel row; cells with span > 1 are merged.
 */
export const addGroupedHeaderRows = (
  worksheet: Worksheet,
  headerLevels: HeaderCell[][],
  totalColumns: number,
): void => {
  headerLevels.forEach((level) => {
    const rowValues = new Array<string>(totalColumns).fill("");
    level.forEach((cell) => {
      rowValues[cell.colStart - 1] = cell.label;
    });

    const excelRow = worksheet.addRow(rowValues);
    excelRow.font = { bold: true };

    level.forEach((cell) => {
      if (cell.span > 1) {
        worksheet.mergeCells(
          excelRow.number,
          cell.colStart,
          excelRow.number,
          cell.colStart + cell.span - 1,
        );
      }
      excelRow.getCell(cell.colStart).alignment = {
        horizontal: cell.colStart === 1 ? "left" : "center",
        vertical: "middle",
      };
    });
  });
};
